import { Fragment, useState } from 'react'
import { ItemDetails, LicenseDetailsCard, Switch } from 'components'
import { Box, Typography } from '@mui/material'
import { CollapsibleList } from 'packages/eid-ui'
import {
    useAzureLicenseServicePlans,
    useCheckAzureLicenseAccess,
    useTargetPerson,
} from 'hooks'
import { useTranslation } from 'react-i18next'
import cartHelpers from 'containers/Cart/cartHelpers'
import useSubcomponents from 'useSubcomponents'
import { OverviewSection } from './OverviewSection'
import { AddToCart } from 'components/AddToCart'
import { ResourceTypesNamespace, useRegistry } from 'core'
import { Skeleton } from '@mui/material'

const azureLicensePrimaryTabExplain = {
    overview:
        'Overview tab: license pool bundle name, tenant, pool, subscription, license group when shown, description, common attributes, and any published instructions.',
}

const AzureLicenseDetails = ({ azureLicense, toggleDrawer }) => {
    const { t } = useTranslation()

    const registry = useRegistry()

    const azureLicenseType = registry.get(
        ResourceTypesNamespace,
        'AzureLicenses',
    )

    const [activeTab, setActiveTab] = useState('overview')

    const [targetPerson] = useTargetPerson()

    const { canSeeAzureLicenseServicePlansSection } = useSubcomponents()

    const { data: servicePlansResponse } = useAzureLicenseServicePlans(
        azureLicense.azureTenantSubscriptionId,
    )

    const { data: assignmentStatus, isInitialLoading: isCheckingAccess } =
        useCheckAzureLicenseAccess(azureLicense.id, targetPerson.id)

    const preAdd = (baseObj) => {
        const itemToAdd = cartHelpers.azLicenseToCartItem({
            targetPerson,
            assignmentType: 'Add',
            azureLicense,
            assignmentStatus,
            ...baseObj,
        })

        if (!itemToAdd) return undefined

        return itemToAdd
    }
    const getPreValidationMessage = () => {
        if (assignmentStatus && assignmentStatus.isAssigned) {
            return t('ResourceAlreadyAssigned')
        }
        return ''
    }

    return (
        <Fragment>
            <ItemDetails.Tabs
                variant="standard"
                value={activeTab}
                onChange={(_, value) => setActiveTab(value)}
            >
                <ItemDetails.Tabs.Tab
                    value="overview"
                    label={t('Common_Overview')}
                    data-ai-explain={azureLicensePrimaryTabExplain.overview}
                />
            </ItemDetails.Tabs>
            {activeTab === 'overview' && (
                <OverviewSection azureLicense={azureLicense} />
            )}

            <Box
                paddingLeft="32px"
                data-ai-explain="High-level license classification for this Azure license product (expandable summary)."
            >
                <CollapsibleList
                    data={{
                        heading: t('AzureLicenses_HighLevelClassification'),
                        list: [
                            {
                                label: azureLicense.highLevelClassification,
                            },
                        ],
                    }}
                    getLabel={(item) => item.label}
                />
            </Box>

            {canSeeAzureLicenseServicePlansSection && servicePlansResponse && (
                <Box
                    padding="24px 30px 16px 32px"
                    marginBottom="15px"
                    borderBottom="1px solid #efeff1"
                    style={{ backgroundColor: '#fbfbfd' }}
                    data-protectedsubcomponent={
                        canSeeAzureLicenseServicePlansSection
                    }
                    data-ai-explain="Microsoft 365 / Azure AD service plans for this license under the selected tenant subscription: each row shows a plan name and whether it is enabled (read-only in the catalog)."
                >
                    <LicenseDetailsCard
                        title={t('AzureLicenses_ServicePlans')}
                        subTitle={
                            azureLicense.azureTenantSubscription_FriendlyName
                        }
                        collapsible={true}
                        expanded={false}
                    >
                        {servicePlansResponse.length === 0 && (
                            <Box
                                display="flex"
                                justifyContent="center"
                                width="100%"
                                data-ai-explain="No service plans were returned for this license subscription."
                            >
                                {t('Common_NoDataFound')}
                            </Box>
                        )}

                        {servicePlansResponse.map((item) => (
                            <Box
                                key={item.id}
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                width="100%"
                            >
                                <Typography
                                    style={{
                                        fontSize: '14px',
                                        wordBreak: 'break-word',
                                        padding: '4px 0px',
                                    }}
                                >
                                    {item.localServiceFriendlyName}
                                </Typography>

                                <Switch
                                    disabled={true}
                                    onLabel={t('Common_On')}
                                    offLabel={t('Common_Off')}
                                    value={!item.disabled}
                                />
                            </Box>
                        ))}
                    </LicenseDetailsCard>
                </Box>
            )}

            {isCheckingAccess ? (
                <Box
                    padding={'24px'}
                    data-ai-explain="Checking whether this Azure license can be requested for the selected person."
                >
                    <Skeleton height={40} />
                </Box>
            ) : (
                <Box
                    data-ai-explain="Add this Azure license to the cart to start an access request when it is not already assigned."
                >
                    <AddToCart
                        preAdd={preAdd}
                        resourceType={azureLicenseType}
                        resource={azureLicense}
                        postAdd={toggleDrawer}
                        preValidate={getPreValidationMessage}
                        showTimeConstraint={false}
                    />
                </Box>
            )}
        </Fragment>
    )
}

export default AzureLicenseDetails
